import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import {
  describeStaffPerformanceError,
  resolveScope,
  selfStaffId,
  subscribeToStaffDoc,
  subscribeToStaffPerformance,
  subscribeToSyncMeta,
} from '../services/staffPerformanceService';
import { StaffPerformanceMeta, StaffPerformanceRecord, StaffPerformanceScope } from '../types';

/** Read scope for the signed-in user; null until auth resolves. */
export function useStaffPerformanceScope(): StaffPerformanceScope | null {
  const { user, userRole, userMetadata } = useAuth();
  return useMemo(() => (user && userRole ? resolveScope(userRole, userMetadata ?? null) : null), [user, userRole, userMetadata]);
}

/** The signed-in user's own staff document id, when their account carries a coach_id. */
export function useSelfStaffId(): string | null {
  const { userMetadata } = useAuth();
  return useMemo(() => selfStaffId(userMetadata), [userMetadata]);
}

interface UseStaffPerformanceResult {
  records: StaffPerformanceRecord[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/** Realtime, role-scoped staff records (team overview / own page). */
export function useStaffPerformance(scope: StaffPerformanceScope | null): UseStaffPerformanceResult {
  const [records, setRecords] = useState<StaffPerformanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const scopeKey = scope ? JSON.stringify(scope) : null;

  useEffect(() => {
    const current = scopeRef.current;
    if (!current) return undefined;

    setIsLoading(true);
    setError(null);

    const unsubscribe = subscribeToStaffPerformance(
      current,
      (next) => {
        setRecords(next);
        setIsLoading(false);
      },
      (subscriptionError) => {
        setError(describeStaffPerformanceError(subscriptionError));
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [scopeKey, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { records, isLoading, error, retry };
}

interface UseStaffDocResult {
  record: StaffPerformanceRecord | null;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
}

/** One staff member's document; `notFound` when the id has no mirror row. */
export function useStaffDoc(staffId: string | undefined): UseStaffDocResult {
  const [record, setRecord] = useState<StaffPerformanceRecord | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(staffId));
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!staffId) {
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    setError(null);
    setNotFound(false);

    const unsubscribe = subscribeToStaffDoc(
      staffId,
      (next) => {
        setRecord(next);
        setNotFound(next === null);
        setIsLoading(false);
      },
      (subscriptionError) => {
        setError(describeStaffPerformanceError(subscriptionError));
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [staffId]);

  return { record, isLoading, error, notFound };
}

interface UseSyncMetaResult {
  meta: StaffPerformanceMeta | null;
  error: string | null;
}

/** Last sync run (for the "Updated" chip and the Admin Panel card). */
export function useSyncMeta(): UseSyncMetaResult {
  const [meta, setMeta] = useState<StaffPerformanceMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToSyncMeta(setMeta, (subscriptionError) => setError(describeStaffPerformanceError(subscriptionError)));
    return () => unsubscribe();
  }, []);

  return { meta, error };
}
